import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Database, Loader2 } from 'lucide-react';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { MemoryRecordDetail } from './MemoryRecordDetail';

export interface MemoryRecord {
    id: string;
    organization_id: string;
    capability_ref?: string;
    app_key?: string;
    user_id?: string;
    scope: string;
    category: string;
    subject_type: string;
    subject_id: string;
    predicate: string;
    object: unknown;
    status: string;
    confidence?: number;
    source?: unknown;
    dedupe_key?: string;
    tags?: string[];
    effective_from?: string;
    effective_to?: string;
    ttl_at?: string;
    created: string;
    created_by?: string;
    modified: string;
    updated_by?: string;
}

interface MemoryRecordsBrowserProps {
    capabilityRef?: string;
}

const SCOPES = ['session', 'workflow', 'user', 'app', 'tenant', 'global'];
const STATUSES = ['proposed', 'confirmed', 'stale', 'superseded', 'disputed'];
const PER_PAGE = 50;

const statusColors: Record<string, string> = {
    proposed: 'bg-amber-100 text-amber-700',
    confirmed: 'bg-green-100 text-green-700',
    stale: 'bg-slate-100 text-slate-500',
    superseded: 'bg-red-100 text-red-600',
    disputed: 'bg-orange-100 text-orange-700',
};

const scopeColors: Record<string, string> = {
    session: 'bg-purple-100 text-purple-700',
    workflow: 'bg-blue-100 text-blue-700',
    user: 'bg-indigo-100 text-indigo-700',
    app: 'bg-teal-100 text-teal-700',
    tenant: 'bg-cyan-100 text-cyan-700',
    global: 'bg-slate-100 text-slate-600',
};

function truncateObject(obj: unknown, maxLen = 60): string {
    const s = typeof obj === 'string' ? obj : JSON.stringify(obj);
    if (!s) return '—';
    return s.length > maxLen ? s.slice(0, maxLen) + '…' : s;
}

function formatDate(iso: string): string {
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return iso;
    }
}

export function MemoryRecordsBrowser({ capabilityRef }: MemoryRecordsBrowserProps) {
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [scope, setScope] = useState<string>('');
    const [category, setCategory] = useState<string>('');
    const [status, setStatus] = useState<string>('');
    const [selectedRecord, setSelectedRecord] = useState<MemoryRecord | null>(null);

    const params: Record<string, string | number> = { page, limit: PER_PAGE };
    if (capabilityRef) params.capability_ref = capabilityRef;
    if (scope) params.scope = scope;
    if (category) params.category = category;
    if (status) params.status = status;

    const { data, isLoading } = useQuery({
        queryKey: ['memory-records', params],
        queryFn: () => api.get('/memory-records', { params }).then(r => r.data),
    });

    const records: MemoryRecord[] = data?.data ?? [];
    const meta = data?.meta ?? { current_page: 1, last_page: 1, total: 0 };

    const resetFilters = useCallback(() => {
        setScope('');
        setCategory('');
        setStatus('');
        setPage(1);
    }, []);

    const handleFilterChange = useCallback((setter: (v: string) => void) => {
        return (value: string) => {
            setter(value === '__all__' ? '' : value);
            setPage(1);
        };
    }, []);

    const handleStatusUpdated = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['memory-records'] });
    }, [queryClient]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Filter bar */}
            <div className="flex flex-wrap items-center gap-3">
                <Select value={scope || '__all__'} onValueChange={handleFilterChange(setScope)}>
                    <SelectTrigger className="w-[140px] h-8 text-xs">
                        <SelectValue placeholder="Scope" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all__">All scopes</SelectItem>
                        {SCOPES.map(s => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Select value={status || '__all__'} onValueChange={handleFilterChange(setStatus)}>
                    <SelectTrigger className="w-[150px] h-8 text-xs">
                        <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all__">All statuses</SelectItem>
                        {STATUSES.map(s => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <input
                    type="text"
                    placeholder="Filter by category…"
                    value={category}
                    onChange={e => { setCategory(e.target.value); setPage(1); }}
                    className="h-8 w-[200px] rounded-md border border-slate-200 bg-white px-3 text-xs placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                />

                {(scope || status || category) && (
                    <Button variant="ghost" size="sm" onClick={resetFilters} className="h-8 text-xs text-slate-500">
                        Clear filters
                    </Button>
                )}

                <span className="ml-auto text-xs text-slate-400">
                    {meta.total} record{meta.total !== 1 ? 's' : ''}
                </span>
            </div>

            {/* Table */}
            {records.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                    <Database className="h-10 w-10 mb-3 opacity-40" />
                    <p className="text-sm font-medium">No memory records found</p>
                    <p className="text-xs mt-1">Records will appear here when agents write to memory</p>
                </div>
            ) : (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="text-xs">Subject</TableHead>
                            <TableHead className="text-xs">Predicate</TableHead>
                            <TableHead className="text-xs">Object</TableHead>
                            <TableHead className="text-xs">Scope</TableHead>
                            <TableHead className="text-xs">Status</TableHead>
                            <TableHead className="text-xs">Category</TableHead>
                            <TableHead className="text-xs">Confidence</TableHead>
                            <TableHead className="text-xs">Updated</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {records.map(rec => (
                            <TableRow
                                key={rec.id}
                                className="cursor-pointer"
                                onClick={() => setSelectedRecord(rec)}
                            >
                                <TableCell className="text-xs font-medium max-w-[160px] truncate">
                                    <span className="text-slate-400">{rec.subject_type}/</span>
                                    {rec.subject_id}
                                </TableCell>
                                <TableCell className="text-xs max-w-[140px] truncate">{rec.predicate}</TableCell>
                                <TableCell className="text-xs max-w-[200px] truncate text-slate-600">
                                    {truncateObject(rec.object)}
                                </TableCell>
                                <TableCell>
                                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${scopeColors[rec.scope] ?? 'bg-slate-100 text-slate-600'}`}>
                                        {rec.scope}
                                    </span>
                                </TableCell>
                                <TableCell>
                                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${statusColors[rec.status] ?? 'bg-slate-100 text-slate-600'}`}>
                                        {rec.status}
                                    </span>
                                </TableCell>
                                <TableCell className="text-xs text-slate-500 max-w-[120px] truncate">{rec.category}</TableCell>
                                <TableCell className="text-xs text-slate-500">
                                    {rec.confidence != null ? `${Math.round(rec.confidence * 100)}%` : '—'}
                                </TableCell>
                                <TableCell className="text-xs text-slate-400">{formatDate(rec.modified)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            {/* Pagination */}
            {meta.last_page > 1 && (
                <div className="flex items-center justify-between pt-2">
                    <span className="text-xs text-slate-400">
                        Page {meta.current_page} of {meta.last_page}
                    </span>
                    <div className="flex items-center gap-1">
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-7 w-7 p-0"
                            disabled={page <= 1}
                            onClick={() => setPage(p => p - 1)}
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-7 w-7 p-0"
                            disabled={page >= meta.last_page}
                            onClick={() => setPage(p => p + 1)}
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}

            {/* Detail drawer */}
            <MemoryRecordDetail
                record={selectedRecord}
                onClose={() => setSelectedRecord(null)}
                onStatusUpdated={handleStatusUpdated}
            />
        </div>
    );
}
